const resourceInfoTable = [
  { res: 'accounts', ref: 'account' },
  { res: 'access--groups', ref: 'access_group' },
  { res: 'access--group-templates', ref: 'access_group_template' },
  { res: 'agreement--templates', ref: 'agreement_template' },
  { res: 'applications', ref: 'application' },
  { res: 'application--user-activities', ref: 'user_activity' },
  { res: 'banking--accounts', ref: 'bank_account', refAliases: ['banking_account'] },
  { res: 'banking--transactions', ref: 'banking_transaction' },
  { res: 'banking--transaction-files', ref: 'transaction_file' },
  { res: 'banking--reversal-handlings', ref: 'banking_reversal_handling' },
  { res: 'cases', ref: 'case', listRef: 'cases', isSettlementDocument: true },
  { res: 'case--debt-collections', ref: 'debt_collection', listRef: 'debt_collections', isSettlementDocument: true },
  { res: 'case--enforcements', ref: 'enforcement', listRef: 'enforcements', isSettlementDocument: true },
  { res: 'case--registrations', ref: 'case_registration' },
  {
    res: 'case--foreign-debt-collections',
    ref: 'foreign_debt_collection',
    listRef: 'foreign_debt_collections',
    isSettlementDocument: true,
  },
  { res: 'case--foreign-enforcements', ref: 'foreign_enforcement' },
  { res: 'case--documents', ref: 'case_document' },
  { res: 'creditors', ref: 'creditor' },
  { res: 'customers', ref: 'customer' },
  { res: 'customer--disputes', ref: 'dispute', listRef: 'disputes', isSettlementDocument: true },
  { res: 'customer--dispute-processes', ref: 'dispute_process' },
  { res: 'customer--users', ref: 'customer_user' },
  { res: 'customer--amortizations', ref: 'amortization', listRef: 'amortizations', isSettlementDocument: true },
  { res: 'customer--amortization-arrangements', ref: 'amortization_arrangement' },
  { res: 'customer--amortization-advices', ref: 'amortization_advice' },
  { res: 'customer--documents', ref: 'customer_document' },
  { res: 'debtors', ref: 'debtor' },
  { res: 'debtor--accords', ref: 'accord' },
  { res: 'debtor--debt-restructurings', ref: 'debt_restructuring', listRef: 'debt_restructurings' },
  { res: 'debtor--debt-surveillances', ref: 'debt_surveillance', listRef: 'debt_surveillances' },
  { res: 'debtor--estates', ref: 'estate', listRef: 'estates' },
  { res: 'debtor--bankruptcies', ref: 'bankruptcy' },
  { res: 'debtor--bankruptcy-estates', ref: 'bankruptcy_estate', listRef: 'bankruptcy_estates' },
  { res: 'debtor--documents', ref: 'debtor_document' },
  { res: 'decisions', ref: 'decision' },
  { res: 'decision--actions-templates', ref: 'actions_template' },
  { res: 'decision--flow-templates', ref: 'flow_template' },
  { res: 'decision--groups', ref: 'decision_group' },
  { res: 'document--distributions', ref: 'document_distribution' },
  { res: 'finance--overpayments', ref: 'finance_overpayment' },
  { res: 'finance--payouts', ref: 'finance_payout' },
  { res: 'finance--refunds', ref: 'finance_refund' },
  { res: 'finance--credits', ref: 'finance_credit' },
  { res: 'invoices', ref: 'invoice' },
  { res: 'invoice--registrations', ref: 'invoice_registration' },
  { res: 'ocr-numbers', ref: 'ocr_number' },
  { res: 'partners', ref: 'partner' },
  { res: 'prints', ref: 'print' },
  { res: 'print--protected-identities', ref: 'print_protected_identity' },
  {
    res: 'public-record--swedish-company-registrations',
    ref: 'public_record_company',
  },
  {
    res: 'public-record--swedish-person-registrations',
    ref: 'public_record_person',
  },
  { res: 'users', ref: 'user', refAliases: ['working_agent'] },
] as const

type Entry = (typeof resourceInfoTable)[number]
type RefAliases<T> = T extends { refAliases: readonly string[] } ? T['refAliases'][number] : never
type ListRefs<T> = T extends { listRef: string } ? T['listRef'] : never

export type Reference = Entry['ref'] | RefAliases<Entry>
export type ReferenceableResource = Entry['res']
export type ListReference = ListRefs<Entry>

type FilterSettlementDocumentTypes<T> = T extends { isSettlementDocument: true } ? T : never
export type SettlementDocumentInfo = FilterSettlementDocumentTypes<(typeof resourceInfoTable)[number]>
export type SettlementDocumentResource = SettlementDocumentInfo['res']

export function referenceToResource(reference: Reference): ReferenceableResource {
  const entry = resourceInfoTable.find(
    (r) => r.ref === reference || ('refAliases' in r && (r.refAliases as readonly string[]).includes(reference))
  )
  if (entry == null) throw new Error(`No resource found for reference ${reference}`)
  return entry.res
}

export function listReferenceToResource(listReference: ListReference): ReferenceableResource {
  const entry = resourceInfoTable.find(
    (r) =>
      ('listRef' in r && r.listRef === listReference) ||
      ('listRefAliases' in r && (r.listRefAliases as readonly string[]).includes(listReference))
  )
  if (entry == null) throw new Error(`No resource found for list reference ${listReference}`)
  return entry.res
}

export function resourceToReference(resource: ReferenceableResource): Reference {
  const ref = resourceInfoTable.find((r) => r.res === resource)?.ref
  if (ref == null) throw new Error(`No reference found for resource ${resource}`)
  return ref
}

export function resourceToListReference(resource: ReferenceableResource): ListReference | undefined {
  const info = resourceInfoTable.find((r) => r.res === resource)
  if (info == null) throw new Error(`No info found for resource ${resource}`)
  if (!('listRef' in info)) return undefined
  return info.listRef
}

export function isReferenceableResource(resource: string): resource is ReferenceableResource {
  return resourceInfoTable.some((r) => r.res === resource)
}

// Some resources display under a reference alias in the shared `state` chip enum
// (e.g. a case shows as "reminder"). Maps a resource to that reference key,
// falling back to its plain reference (or the resource name for non-referenceable
// resources). Used by the coloured reference-number helper (useReferenceChip).
const REFERENCE_TRANSLATION_KEY_OVERRIDES: Readonly<Record<string, string>> = {
  cases: 'reminder',
}

export function getResourceTranslationKey(resource: string): string {
  const override = REFERENCE_TRANSLATION_KEY_OVERRIDES[resource]
  if (override != null) return override
  if (isReferenceableResource(resource)) return resourceToReference(resource)
  return resource
}

// Most specific first: settlement documents in reverse table order, other resources last.
const REFERENCE_SPECIFICITY_ORDER: readonly string[] = getSettlementDocumentResources().toReversed()

export function getReferenceSpecificityRank(resource: string): number {
  const rank = REFERENCE_SPECIFICITY_ORDER.indexOf(resource)
  return rank === -1 ? REFERENCE_SPECIFICITY_ORDER.length : rank
}

export function isReference(resource: string): resource is Reference {
  return resourceInfoTable.some(
    (r) => r.ref === resource || ('refAliases' in r && (r.refAliases as readonly string[]).includes(resource))
  )
}

export function isListReference(value: string): value is ListReference {
  return resourceInfoTable.some(
    (r) =>
      ('listRef' in r && r.listRef === value) ||
      ('listRefAliases' in r && (r.listRefAliases as readonly string[]).includes(value))
  )
}

/**
 * Get all settlement document resources.
 * Settlement documents are resources that participate in debt summaries.
 */
export function getSettlementDocumentResources(): readonly SettlementDocumentResource[] {
  return resourceInfoTable.filter((r): r is SettlementDocumentInfo => 'isSettlementDocument' in r).map((r) => r.res)
}

export function isSettlementDocumentResource(resource: string): resource is SettlementDocumentResource {
  return resourceInfoTable.some((r) => r.res === resource && 'isSettlementDocument' in r)
}
